import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PERIODS = [
  ["today", "Today"],
  ["week", "This week"],
  ["month", "This month"],
];

const inr = (amount) => `₹${Number(amount || 0).toLocaleString("en-IN", {
  maximumFractionDigits: 0,
})}`;

export default function MarginAnalytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics/margins")
      .then(({ data: response }) => setData(response))
      .catch((error) => {
        setData({ periods: {}, error: true });
        toast.error(error?.response?.data?.detail || "Could not load margin analytics.");
      });
  }, []);

  if (!data) {
    return <div className="mt-10 label-xs text-espresso-light">Loading…</div>;
  }

  if (data.error) {
    return (
      <div
        className="mt-10 border-l-2 border-rose-700 bg-rose-50 px-4 py-3 text-sm text-rose-800"
        data-testid="margin-analytics-error"
      >
        Margin analytics could not be loaded. Please try again.
      </div>
    );
  }

  return (
    <section className="mt-8" data-testid="margin-analytics-panel">
      <div className="max-w-3xl">
        <p className="label-xs text-gold-hover">Owner ledger</p>
        <h2 className="mt-2 font-serif text-3xl text-espresso">Margin analytics</h2>
        <p className="mt-2 text-sm text-espresso-light">
          Website food revenue, less gateway fees, product COGS, and internal delivery cost.
        </p>
      </div>

      <div className="mt-8 grid gap-5 md:grid-cols-3">
        {PERIODS.map(([key, label]) => {
          const period = data.periods?.[key] || {};
          return (
            <article
              key={key}
              className={
                "border border-line bg-ivory p-6 transition-[transform,border-color] "
                + "duration-200 hover:-translate-y-0.5 hover:border-espresso"
              }
              data-testid={`margin-period-${key}`}
            >
              <p className="label-xs text-espresso-light">Total shop profit · {label}</p>
              <p
                className={`mt-3 font-serif text-4xl ${
                  period.net_profit >= 0 ? "text-emerald-800" : "text-rose-700"
                }`}
                data-testid={`margin-profit-${key}`}
              >
                {inr(period.net_profit)}
              </p>
              <p
                className="mt-2 text-xs text-espresso-light"
                data-testid={`margin-rate-${key}`}
              >
                {Number(period.margin_percent || 0).toFixed(1)}% net margin · {period.orders || 0}
                {" "}orders
              </p>
              <dl
                className="mt-6 space-y-2 border-t border-line pt-4 text-xs text-espresso-light"
              >
                <div className="flex justify-between gap-3">
                  <dt>Website revenue</dt>
                  <dd data-testid={`margin-revenue-${key}`}>{inr(period.revenue)}</dd>
                </div>
                <div className="flex justify-between gap-3 text-amber-800">
                  <dt>Gateway fees · 3%</dt>
                  <dd data-testid={`margin-gateway-${key}`}>−{inr(period.gateway_fee)}</dd>
                </div>
                <div className="flex justify-between gap-3 text-espresso">
                  <dt>Charged Customer</dt>
                  <dd data-testid={`margin-charged-customer-${key}`}>
                    {inr(period.charged_customer_delivery_fee)}
                  </dd>
                </div>
                <div className="flex justify-between gap-3 text-rose-700">
                  <dt>Product COGS</dt>
                  <dd data-testid={`margin-cogs-${key}`}>−{inr(period.total_cogs)}</dd>
                </div>
                <div className="flex justify-between gap-3 text-sky-800">
                  <dt>Estimated Actual Cost</dt>
                  <dd data-testid={`margin-delivery-${key}`}>
                    −{inr(period.actual_delivery_cost)}
                  </dd>
                </div>
                {period.manual_delivery_cost_orders > 0 && (
                  <div className="flex justify-between gap-3 text-emerald-800">
                    <dt>Manual cost estimates</dt>
                    <dd data-testid={`margin-manual-delivery-costs-${key}`}>
                      {period.manual_delivery_cost_orders} order
                      {period.manual_delivery_cost_orders === 1 ? "" : "s"}
                    </dd>
                  </div>
                )}
                {Number(period.launch_cookie_cost || 0) > 0 && (
                  <div className="flex justify-between gap-3 text-amber-800">
                    <dt>Launch cookie cost</dt>
                    <dd data-testid={`margin-cookie-cost-${key}`}>
                      −{inr(period.launch_cookie_cost)}
                    </dd>
                  </div>
                )}
                {period.road_cost_pending_orders > 0 && (
                  <div className="flex justify-between gap-3 text-amber-800">
                    <dt>Road routes pending</dt>
                    <dd data-testid={`margin-road-pending-${key}`}>
                      {period.road_cost_pending_orders} order
                      {period.road_cost_pending_orders === 1 ? "" : "s"}
                    </dd>
                  </div>
                )}
              </dl>
            </article>
          );
        })}
      </div>
    </section>
  );
}